import './css/app.css';
import './css/main.css';
import './css/common.css';
import './css/payment.css';
import './css/mobile.css';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LoadingModal, MessageModal } from './component/modal/modal';
import { filterInstallmentOptions, getPolicyNotice, isQuotaAllowed } from './common/manualCardPolicy';


function SmsPaymentPublic() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // 카드번호 입력 필드 ref
    const cardPart2Ref = useRef(null);
    const cardPart3Ref = useRef(null);
    const cardPart4Ref = useRef(null);

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ msg: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 복호화된 결제 데이터
    const [paymentData, setPaymentData] = useState({
        productName: "",
        amount: "",
        mid: "",
    });

    // URL 파라미터에서 암호화된 데이터 받기 (원본 보관용)
    const encryptedData = searchParams.get("data") || "";

    // 폼 데이터 (sugiManage와 동일한 구조)
    const [formData, setFormData] = useState({
        BuyerName: "",
        BuyerHpTel: "",
        BuyerEmail: "",
        card_num: "",
        CardCvv: "",
        CardPw: "",
        ExpMonth: "",
        ExpYear: "",
        card_gubun: "개인",
        IDNo: "",
        CardQuota: "00",
    });

    const [phoneDisplayValue, setPhoneDisplayValue] = useState("");
    const [cardPart1, setCardPart1] = useState(""); // 앞 4자리
    const [cardPart2, setCardPart2] = useState(""); // 5-8자리 (password)
    const [cardPart3, setCardPart3] = useState(""); // 9-12자리 (password)
    const [cardPart4, setCardPart4] = useState(""); // 뒤 4자리
    const [bank, setBank] = useState("");
    const [cardPolicy, setCardPolicy] = useState(null);
    const [cardPolicyChecking, setCardPolicyChecking] = useState(false);

    const quotaOptions = [
        { value: "00", label: "일시불" },
        ...Array.from({ length: 12 }, (_, i) => {
            const month = i + 1;
            const padded = String(month).padStart(2, "0");
            return { value: padded, label: `${month}개월` };
        }),
    ];

    // 암호화된 데이터 복호화
    useEffect(() => {
        if (!encryptedData) {
            setMessage({ msg: "결제 정보가 없습니다." });
            setTimeout(() => {
                navigate("/");
            }, 2000);
            return;
        }

        // 백엔드 API로 복호화 요청
        const decryptData = async () => {
            try {
                const response = await axios.get(
                    process.env.REACT_APP_API + "/api/sms/decrypt",
                    {
                        params: { data: encryptedData },
                        withCredentials: true
                    }
                );

                if (response.data?.success && response.data?.data) {
                    const decryptPayload = response.data.data;
                    const { productName, amount } = decryptPayload;
                    const mid = decryptPayload.mid
                        || decryptPayload.MID
                        || decryptPayload.merchantMid
                        || decryptPayload.merchant_id
                        || decryptPayload.merchant?.mid
                        || "";

                    setPaymentData({
                        productName: productName || "",
                        amount: String(amount || ""),
                        mid,
                    });
                    
                    setLoading(false);
                } else {
                    setMessage({ msg: response.data?.msg || "결제 정보 복호화에 실패했습니다." });
                    setTimeout(() => {
                        navigate("/");
                    }, 2000);
                }
            } catch (error) {
                console.error("복호화 오류:", error);
                setMessage({ msg: "결제 정보를 불러오는 중 오류가 발생했습니다." });
                setTimeout(() => {
                    navigate("/");
                }, 2000);
            }
        };

        decryptData();
    }, [encryptedData, navigate]);

    // 전화번호 입력 핸들러
    const handlePhoneChange = (e) => {
        let numeric = e.target.value.replace(/\D/g, "").slice(0, 11);
        const parts =
            numeric.length < 4
                ? [numeric]
                : numeric.length < 8
                    ? [numeric.slice(0, 3), numeric.slice(3)]
                    : [numeric.slice(0, 3), numeric.slice(3, 7), numeric.slice(7)];
        const displayVal = parts.join("-");
        setPhoneDisplayValue(displayVal);
        setFormData((prev) => ({ ...prev, BuyerHpTel: numeric }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // 카드번호 입력 핸들러 (4개 필드 분리)
    const handleCardPartChange = (part, value, nextInputRef) => {
        const numericVal = value.replace(/\D/g, "").slice(0, 4);
        
        // 각 필드 업데이트 및 전체 카드번호 조합
        let newPart1 = cardPart1;
        let newPart2 = cardPart2;
        let newPart3 = cardPart3;
        let newPart4 = cardPart4;
        
        if (part === 1) {
            newPart1 = numericVal;
            setCardPart1(numericVal);
            if (numericVal.length === 4 && nextInputRef) {
                nextInputRef.current?.focus();
            }
        } else if (part === 2) {
            newPart2 = numericVal;
            setCardPart2(numericVal);
            if (numericVal.length === 4 && nextInputRef) {
                nextInputRef.current?.focus();
            }
        } else if (part === 3) {
            newPart3 = numericVal;
            setCardPart3(numericVal);
            if (numericVal.length === 4 && nextInputRef) {
                nextInputRef.current?.focus();
            }
        } else if (part === 4) {
            newPart4 = numericVal;
            setCardPart4(numericVal);
        }

        // 전체 카드번호 조합
        const newFullCardNumber = newPart1 + newPart2 + newPart3 + newPart4;
        
        setFormData((prev) => ({ ...prev, card_num: newFullCardNumber }));

        // 카드사 조회 (8자리 이상일 때)
        if (newFullCardNumber.length >= 8) {
            CheckBank(newFullCardNumber.slice(0, 8));
        } else {
            setBank("");
        }
    };

    // 백스페이스 처리 (이전 필드로 포커스 이동)
    const handleCardPartKeyDown = (e, part, prevInputRef) => {
        if (e.key === "Backspace" && e.target.value === "" && prevInputRef) {
            prevInputRef.current?.focus();
        }
    };

    // 카드사 조회
    const CheckBank = async (eightDigits) => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_API + "/api/card-info/check",
                {
                    withCredentials: true,
                    params: { cardNumber: eightDigits },
                }
            );
            let tmp = response?.data;
            if (tmp?.code < 2000) {
                setBank(tmp?.data[0]?.card ?? "");
            } else {
                setBank("유효하지 않습니다.");
            }
        } catch (error) {
            console.error(error);
            setBank("");
        }
    };

    const CheckCardPolicy = async (cardNumber) => {
        if (!paymentData.mid || cardNumber.length < 8) {
            setCardPolicy(null);
            return;
        }
        setCardPolicyChecking(true);
        try {
            const response = await axios.post(
                process.env.REACT_APP_API + "/api/sugi/public/card-policy/check",
                { mid: paymentData.mid, cardBin: cardNumber.slice(0, 8) }
            );
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                setCardPolicy(tmp?.data ?? null);
            } else {
                setCardPolicy(null);
            }
        } catch (error) {
            console.error(error);
            setCardPolicy(null);
        } finally {
            setCardPolicyChecking(false);
        }
    };

    useEffect(() => {
        if (formData.card_num.length >= 8) {
            CheckCardPolicy(formData.card_num);
        } else {
            setCardPolicy(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.card_num, paymentData.mid]);

    // 필수값 체크 (sugiManage와 동일)
    const requiredFilled =
        (formData.BuyerName || "").trim() !== "" &&
        (formData.BuyerHpTel || "").trim() !== "" &&
        formData.BuyerHpTel.length >= 10 &&
        (formData.BuyerEmail || "").trim() !== "" &&
        (formData.card_num || "").trim() !== "" &&
        formData.card_num.length === 16 &&
        (formData.CardPw || "").trim() !== "" &&
        formData.CardPw.length === 2 &&
        (formData.ExpMonth || "").trim() !== "" &&
        (formData.ExpYear || "").trim() !== "" &&
        (formData.IDNo || "").trim() !== "" &&
        (formData.CardCvv || "").trim() !== "";

    const currentQuotaOptions = filterInstallmentOptions(quotaOptions, cardPolicy?.max_installment);
    const cardPolicyNotice = getPolicyNotice(cardPolicy);
    const isCardBlocked = cardPolicy?.usable === false;
    const isDisabled = isSubmitting || !requiredFilled || isCardBlocked || cardPolicyChecking;

    useEffect(() => {
        if (!isQuotaAllowed(formData.CardQuota, cardPolicy?.max_installment)) {
            setFormData((prev) => ({ ...prev, CardQuota: "00" }));
        }
    }, [cardPolicy?.max_installment, formData.CardQuota]);

    // 금액 표시용 (천단위 콤마)
    const displayAmount = paymentData.amount ? parseInt(paymentData.amount, 10).toLocaleString("ko-KR") : "";

    // 결제 요청
    const onSubmitPayment = async () => {
        if (isDisabled) {
            if (isCardBlocked) setMessage({ msg: cardPolicyNotice || "해당 카드는 사용할 수 없습니다." });
            return;
        }
        setIsSubmitting(true);
        setLoading(true);

        try {
            const amount = parseInt(paymentData.amount, 10) || 0;
            
            // 카드 유효기간을 YYMM 형식으로 변환
            const expMonth = formData.ExpMonth.padStart(2, '0');
            const expYear = formData.ExpYear.length === 2 ? formData.ExpYear : formData.ExpYear.slice(-2);
            const cardExpiry = expYear + expMonth; // YYMM 형식
            
            // 가이드에 맞춘 요청 파라미터
            const response = await axios.post(
                process.env.REACT_APP_API + "/api/sugi/public",
                {
                    mid: paymentData.mid || "",
                    payerName: formData.BuyerName,
                    payerEmail: formData.BuyerEmail || " ",
                    payerTel: formData.BuyerHpTel,
                    cardNumber: formData.card_num,
                    cardExpiry: cardExpiry, // MMYY 형식
                    cardCvv: formData.CardCvv,
                    installment: formData.CardQuota, // 문자열 형태로 전달
                    products: [
                        {
                            name: paymentData.productName || "",
                            quantity: 1,
                            price: amount
                        }
                    ],
                    metadata: {
                        cardAuth: "true",
                        authPw: formData.CardPw,
                        authDob: formData.IDNo,
                    },
                    amount: amount
                },
                { 
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            const tmp = response?.data;

            // 성공 응답 처리 (code === 1000)
            if (tmp?.code === 1000) {
                setLoading(false);
                setIsSubmitting(false);
                setMessage({ msg: "결제가 완료되었습니다." });
                // TODO: 결제 완료 페이지로 이동하거나 상태 확인
            } else {
                // 실패 응답 처리
                console.error("결제 실패:", {
                    code: tmp?.code,
                    msg: tmp?.msg,
                    fullResponse: tmp
                });
                setLoading(false);
                setIsSubmitting(false);
                setMessage({ msg: tmp?.msg || "결제에 실패했습니다." });
            }
        } catch (error) {
            console.error("결제 요청 오류:", error);
            setLoading(false);
            setIsSubmitting(false);
            const apiMsg = error?.response?.data?.msg || error?.message;
            setMessage({ msg: apiMsg ? `오류: ${apiMsg}` : "결제 요청 중 오류가 발생했습니다." });
        }
    };

    // 결제창 열기 (paymentUrl 직접 사용)
    const openPaymentWindow = (paymentUrl) => {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        let newWindow;
        if (isMobile) {
            newWindow = window.open(
                'about:blank',
                '_blank',
                `width=${window.innerWidth},height=${window.innerHeight},left=0,top=0`
            );
        } else {
            const width = 500;
            const height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            newWindow = window.open(
                'about:blank',
                '_blank',
                `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
            );
        }

        const popupBlocked = !newWindow || typeof newWindow.closed === 'undefined';
        if (popupBlocked) {
            alert("사파리가 결제 팝업을 차단했습니다.\nSafari 설정 > 팝업 차단 해제를 해주시거나\n'결제창 열기' 버튼을 다시 눌러주세요.");
            setLoading(false);
            setIsSubmitting(false);
            return;
        }

        newWindow.document.write('<p style="font:16px/1.4 -apple-system">결제창을 준비 중입니다...</p>');
        newWindow.document.close();

        // 팝업 닫힘 감지
        const popupCheckInterval = setInterval(() => {
            if (newWindow.closed) {
                clearInterval(popupCheckInterval);
                setLoading(false);
                setIsSubmitting(false);
            }
        }, 500);

        // paymentUrl로 이동
        newWindow.location.href = paymentUrl;
        setLoading(false);
        setIsSubmitting(false);
    };

    const onSubmitPaymentModal = async (header, body) => {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        let newWindow;
        if (isMobile) {
            newWindow = window.open(
                'about:blank',
                '_blank',
                `width=${window.innerWidth},height=${window.innerHeight},left=0,top=0`
            );
        } else {
            const width = 500;
            const height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            newWindow = window.open(
                'about:blank',
                '_blank',
                `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
            );
        }

        const popupBlocked = !newWindow || typeof newWindow.closed === 'undefined';
        if (popupBlocked) {
            alert("사파리가 결제 팝업을 차단했습니다.\nSafari 설정 > 팝업 차단 해제를 해주시거나\n'결제창 열기' 버튼을 다시 눌러주세요.");
            return;
        }

        newWindow.document.write('<p style="font:16px/1.4 -apple-system">결제창을 준비 중입니다...</p>');
        newWindow.document.close();

        const popupCheckInterval = setInterval(() => {
            if (newWindow.closed) {
                clearInterval(popupCheckInterval);
                onSubmitPaymentCheck(header, body);
            }
        }, 500);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_PG_API}/api/payment/request`,
                JSON.stringify(body),
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: header?.Authorization ?? "",
                    },
                }
            );

            const tmp = response?.data;

            if (tmp?.success && tmp?.paymentUrl) {
                // paymentUrl이 현재 페이지를 가리키는지 확인
                if (tmp.paymentUrl.includes('/urlPayment/sms') || tmp.paymentUrl.includes('/sms?')) {
                    newWindow.close();
                    setLoading(false);
                    setIsSubmitting(false);
                    setMessage({ msg: "결제 URL이 올바르지 않습니다." });
                } else {
                    newWindow.location.href = tmp.paymentUrl;
                }
            } else {
                newWindow.close();
                setLoading(false);
                setIsSubmitting(false);
                setMessage({ msg: tmp?.msg || "결제실패" });
            }
        } catch (error) {
            console.error(error);
            newWindow.close();
            const apiMsg = error?.response?.data?.msg || error?.message;
            setMessage({ msg: apiMsg ? `오류: ${apiMsg}` : "결제 요청 중 오류가 발생했습니다." });
        }
    };

    const onSubmitPaymentCheck = async (header, body) => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_PG_API + `/api/payment/status/${body?.tid ?? ""}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: header?.Authorization ?? "",
                    },
                }
            );

            let tmp = response?.data;
            if (tmp?.success) {
                navigate("/urlComplete", {
                    state: {
                        receipt: tmp,
                            listData: { product_name: paymentData.productName, price: paymentData.amount },
                    },
                    replace: false,
                });
            } else {
                setLoading(false);
                setIsSubmitting(false);
                setMessage({ msg: "결제실패" });
            }
        } catch (error) {
            console.error(error);
            setLoading(false);
            setIsSubmitting(false);
            const apiMsg = error?.response?.data?.msg || error?.message;
            setMessage({ msg: apiMsg ? `오류: ${apiMsg}` : "결제 요청 중 오류가 발생했습니다." });
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {loading ? <LoadingModal /> : null}
            <MessageModal message={message} />

            <div style={{ 
                minHeight: '100vh', 
                minHeight: '100dvh',
                background: "#f7f8fa",
                padding: '0 0 calc(100px + env(safe-area-inset-bottom))',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale'
            }}>
                {/* 상단 헤더 */}
                <header style={{
                    background: '#fff',
                    borderBottom: '1px solid #e8eaed',
                    padding: '14px 0',
                    paddingTop: 'calc(14px + env(safe-area-inset-top))',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                }}>
                    <div style={{ width: '100%', margin: '0 auto', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                background: 'linear-gradient(135deg, #03c75a 0%, #02b350 100%)',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontWeight: '700',
                                fontSize: '18px',
                                boxShadow: '0 2px 4px rgba(3, 199, 90, 0.2)'
                            }}>
                                P
                            </div>
                            <div>
                                <h1 style={{ 
                                    fontSize: '17px', 
                                    fontWeight: '700', 
                                    color: '#000',
                                    margin: 0,
                                    lineHeight: '1.3',
                                    letterSpacing: '-0.3px'
                                }}>
                                    주식회사 빠른
                                </h1>
                                <p style={{ 
                                    fontSize: '11px', 
                                    color: '#8e8e93', 
                                    margin: 0,
                                    lineHeight: '1.3'
                                }}>
                                    안전한 결제 서비스
                                </p>
                            </div>
                        </div>
                        <div style={{
                            fontSize: '11px',
                            color: '#666',
                            padding: '5px 10px',
                            background: '#f0f0f0',
                            borderRadius: '10px',
                            fontWeight: '500'
                        }}>
                            SMS수기결제
                        </div>
                    </div>
                </header>

                <div style={{ width: '100%', margin: '0 auto', padding: '20px 16px' }}>
                    {/* 페이지 제목 */}
                    <div style={{ 
                        marginBottom: '20px'
                    }}>
                        <h2 style={{ 
                            fontSize: '20px', 
                            fontWeight: '700', 
                            color: '#000',
                            margin: '0 0 6px 0',
                            letterSpacing: '-0.3px'
                        }}>
                            결제하기
                        </h2>
                        <p style={{ 
                            fontSize: '13px', 
                            color: '#8e8e93', 
                            margin: 0,
                            lineHeight: '1.5'
                        }}>
                            주문 정보를 확인하고 결제를 진행해주세요
                        </p>
                    </div>

                    {/* 주문상품 */}
                    <div style={{ 
                        background: '#fff', 
                        padding: '18px 16px 16px', 
                        marginBottom: '12px',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        border: '1px solid #e8eaed'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            marginBottom: '16px',
                            paddingBottom: '14px',
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <div style={{
                                width: '4px',
                                height: '16px',
                                background: '#03c75a',
                                borderRadius: '2px',
                                marginRight: '10px'
                            }}></div>
                            <h2 style={{ 
                                fontSize: '16px', 
                                fontWeight: '600', 
                                color: '#000',
                                margin: 0,
                                letterSpacing: '-0.3px'
                            }}>
                                주문상품
                            </h2>
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span style={{ 
                                fontSize: '15px', 
                                color: '#333',
                                fontWeight: '500',
                                lineHeight: '1.5'
                            }}>
                                {paymentData.productName || "상품명"}
                            </span>
                            <span style={{ fontSize: '18px', color: '#000', fontWeight: '700', letterSpacing: '-0.3px' }}>
                                {displayAmount || "0"}원
                            </span>
                        </div>
                    </div>

                    {/* 카드 정보 (sugiManage와 동일한 구조) */}
                    <div style={{ 
                        background: '#fff', 
                        padding: '18px 16px 16px', 
                        marginBottom: '12px',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        border: '1px solid #e8eaed'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            marginBottom: '20px',
                            paddingBottom: '14px',
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <div style={{
                                width: '4px',
                                height: '16px',
                                background: '#03c75a',
                                borderRadius: '2px',
                                marginRight: '10px'
                            }}></div>
                            <h2 style={{ 
                                fontSize: '16px', 
                                fontWeight: '600', 
                                color: '#000',
                                margin: 0,
                                letterSpacing: '-0.3px'
                            }}>
                                결제 정보
                            </h2>
                        </div>

                        {/* 카드번호 (4개 필드 분리) */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '10px'
                            }}>
                                <span>
                                    신용카드번호 <span style={{ color: '#ff3b30' }}>*</span>
                                </span>
                                {(bank || cardPolicyNotice || cardPolicyChecking) && (
                                    <span style={{ 
                                        fontSize: '12px', 
                                        color: isCardBlocked || bank.includes('유효하지') ? '#ff3b30' : '#03c75a',
                                        fontWeight: '600'
                                    }}>
                                        {cardPolicyChecking ? '정책 확인중' : (cardPolicyNotice || bank)}
                                    </span>
                                )}
                            </label>
                            <div style={{ display: 'flex', gap: '2px', alignItems: 'center', width: '100%' }}>
                                {/* 첫 번째 4자리 */}
                                <input
                                    type="text"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    placeholder="1234"
                                    maxLength={4}
                                    value={cardPart1}
                                    onChange={(e) => handleCardPartChange(1, e.target.value, cardPart2Ref)}
                                    onKeyDown={(e) => handleCardPartKeyDown(e, 1, null)}
                                    style={{
                                        flex: '0 0 calc(23% - 2px)',
                                        minWidth: 0,
                                        padding: '10px 2px',
                                        fontSize: '14px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'all 0.2s',
                                        textAlign: 'center',
                                        background: '#fff',
                                        WebkitAppearance: 'none'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#03c75a';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                                <span style={{ fontSize: '11px', color: '#8e8e93', fontWeight: '500', flexShrink: 0, padding: '0', width: '2%', textAlign: 'center' }}>-</span>
                                
                                {/* 두 번째 4자리 (password) */}
                                <input
                                    ref={cardPart2Ref}
                                    type="password"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    placeholder="****"
                                    maxLength={4}
                                    value={cardPart2}
                                    onChange={(e) => handleCardPartChange(2, e.target.value, cardPart3Ref)}
                                    onKeyDown={(e) => handleCardPartKeyDown(e, 2, null)}
                                    style={{
                                        flex: '0 0 calc(23% - 2px)',
                                        minWidth: 0,
                                        padding: '10px 2px',
                                        fontSize: '14px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'all 0.2s',
                                        textAlign: 'center',
                                        background: '#fff',
                                        WebkitAppearance: 'none'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#03c75a';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                                <span style={{ fontSize: '11px', color: '#8e8e93', fontWeight: '500', flexShrink: 0, padding: '0', width: '2%', textAlign: 'center' }}>-</span>
                                
                                {/* 세 번째 4자리 (password) */}
                                <input
                                    ref={cardPart3Ref}
                                    type="password"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    placeholder="****"
                                    maxLength={4}
                                    value={cardPart3}
                                    onChange={(e) => handleCardPartChange(3, e.target.value, cardPart4Ref)}
                                    onKeyDown={(e) => handleCardPartKeyDown(e, 3, cardPart2Ref)}
                                    style={{
                                        flex: '0 0 calc(23% - 2px)',
                                        minWidth: 0,
                                        padding: '10px 2px',
                                        fontSize: '14px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'all 0.2s',
                                        textAlign: 'center',
                                        background: '#fff',
                                        WebkitAppearance: 'none'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#03c75a';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                                <span style={{ fontSize: '11px', color: '#8e8e93', fontWeight: '500', flexShrink: 0, padding: '0', width: '2%', textAlign: 'center' }}>-</span>
                                
                                {/* 네 번째 4자리 */}
                                <input
                                    ref={cardPart4Ref}
                                    type="text"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    placeholder="5678"
                                    maxLength={4}
                                    value={cardPart4}
                                    onChange={(e) => handleCardPartChange(4, e.target.value, null)}
                                    onKeyDown={(e) => handleCardPartKeyDown(e, 4, cardPart3Ref)}
                                    style={{
                                        flex: '0 0 calc(23% - 2px)',
                                        minWidth: 0,
                                        padding: '10px 2px',
                                        fontSize: '14px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'all 0.2s',
                                        textAlign: 'center',
                                        background: '#fff',
                                        WebkitAppearance: 'none'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#03c75a';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                            </div>
                            {(cardPolicyNotice || cardPolicyChecking) && (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '9px 10px',
                                    borderRadius: '8px',
                                    background: isCardBlocked ? '#fff5f5' : '#eff6ff',
                                    border: isCardBlocked ? '1px solid #fecaca' : '1px solid #bfdbfe',
                                    color: isCardBlocked ? '#dc2626' : '#1d4ed8',
                                    fontSize: '12px',
                                    fontWeight: '600'
                                }}>
                                    {cardPolicyChecking ? '카드 정책을 확인 중입니다.' : cardPolicyNotice}
                                </div>
                            )}
                        </div>

                        {/* 카드비밀번호 + CVV + 유효기간 */}
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                {/* 카드비밀번호 */}
                                <div style={{ flex: '0 0 auto' }}>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#333',
                                        marginBottom: '10px'
                                    }}>
                                        카드비밀번호 <span style={{ color: '#ff3b30' }}>*</span>
                                    </label>
                                    <input
                                        name="CardPw"
                                        type="password"
                                        pattern="[0-9]*"
                                        inputMode="numeric"
                                        placeholder="앞2자리"
                                        maxLength={2}
                                        autoComplete="new-password"
                                        value={formData.CardPw}
                                        onChange={handleChange}
                                        style={{
                                            width: '80px',
                                            padding: '14px 8px',
                                            fontSize: '16px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            boxSizing: 'border-box',
                                            outline: 'none',
                                            transition: 'all 0.2s',
                                            textAlign: 'center',
                                            background: '#fff',
                                            WebkitAppearance: 'none'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#03c75a';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#d1d5db';
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>

                                {/* CVV */}
                                <div style={{ flex: '0 0 auto' }}>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#333',
                                        marginBottom: '10px'
                                    }}>
                                        CVV <span style={{ color: '#ff3b30' }}>*</span>
                                    </label>
                                    <input
                                        name="CardCvv"
                                        type="password"
                                        pattern="[0-9]*"
                                        inputMode="numeric"
                                        placeholder="뒷면 3자리"
                                        maxLength={3}
                                        autoComplete="new-password"
                                        value={formData.CardCvv}
                                        onChange={handleChange}
                                        style={{
                                            width: '100px',
                                            padding: '14px 8px',
                                            fontSize: '16px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            boxSizing: 'border-box',
                                            outline: 'none',
                                            transition: 'all 0.2s',
                                            textAlign: 'center',
                                            background: '#fff',
                                            WebkitAppearance: 'none'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#03c75a';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#d1d5db';
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>

                                {/* 카드 유효기간 */}
                                <div style={{ flex: '0 0 auto' }}>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#333',
                                        marginBottom: '10px'
                                    }}>
                                        카드 유효기간 <span style={{ color: '#ff3b30' }}>*</span>
                                    </label>
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                        <input
                                            name="ExpMonth"
                                            type="text"
                                            pattern="[0-9]*"
                                            inputMode="numeric"
                                            placeholder="MM"
                                            maxLength={2}
                                            value={formData.ExpMonth}
                                            onChange={handleChange}
                                            style={{
                                                width: '60px',
                                                padding: '14px 8px',
                                                fontSize: '16px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                boxSizing: 'border-box',
                                                outline: 'none',
                                                transition: 'all 0.2s',
                                                textAlign: 'center',
                                                background: '#fff',
                                                WebkitAppearance: 'none'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#03c75a';
                                                e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#d1d5db';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                        <span style={{ fontSize: '16px', color: '#8e8e93', fontWeight: '500', flexShrink: 0, padding: '0 2px' }}>/</span>
                                        <input
                                            name="ExpYear"
                                            type="text"
                                            pattern="[0-9]*"
                                            inputMode="numeric"
                                            placeholder="YY"
                                            maxLength={2}
                                            value={formData.ExpYear}
                                            onChange={handleChange}
                                            style={{
                                                width: '60px',
                                                padding: '14px 8px',
                                                fontSize: '16px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                boxSizing: 'border-box',
                                                outline: 'none',
                                                transition: 'all 0.2s',
                                                textAlign: 'center',
                                                background: '#fff',
                                                WebkitAppearance: 'none'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#03c75a';
                                                e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#d1d5db';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 구매자명 */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ 
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '10px'
                            }}>
                                구매자명 <span style={{ color: '#ff3b30' }}>*</span>
                            </label>
                            <input
                                name="BuyerName"
                                placeholder="이름을 입력하세요"
                                maxLength={20}
                                value={formData.BuyerName}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    transition: 'all 0.2s',
                                    background: '#fff',
                                    WebkitAppearance: 'none'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#03c75a';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        {/* 휴대폰번호 */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ 
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '10px'
                            }}>
                                휴대폰번호 <span style={{ color: '#ff3b30' }}>*</span>
                            </label>
                            <input
                                name="BuyerHpTel_display"
                                placeholder="010-0000-0000"
                                value={phoneDisplayValue}
                                onChange={handlePhoneChange}
                                style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    transition: 'all 0.2s',
                                    background: '#fff',
                                    WebkitAppearance: 'none'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#03c75a';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        {/* 생년월일/사업자번호 */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ 
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '10px'
                            }}>
                                생년월일/사업자번호 <span style={{ color: '#ff3b30' }}>*</span>
                            </label>
                            <input
                                name="IDNo"
                                type="text"
                                pattern="[0-9]*"
                                inputMode="numeric"
                                placeholder="생년월일 또는 사업자번호"
                                maxLength={13}
                                value={formData.IDNo}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    transition: 'all 0.2s',
                                    background: '#fff',
                                    WebkitAppearance: 'none'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#03c75a';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        {/* 할부기간 */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ 
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '10px'
                            }}>
                                할부기간 <span style={{ color: '#ff3b30' }}>*</span>
                            </label>
                            <select
                                name="CardQuota"
                                value={formData.CardQuota}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    background: '#fff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'12\' height=\'8\' viewBox=\'0 0 12 8\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg%27%3E%3Cpath d=\'M1 1L6 6L11 1\' stroke=\'%23333\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 16px center',
                                    paddingRight: '40px'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#03c75a';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                {currentQuotaOptions.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* 이메일 */}
                        <div style={{ marginBottom: '0' }}>
                            <label style={{ 
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '10px'
                            }}>
                                이메일 <span style={{ color: '#ff3b30' }}>*</span>
                            </label>
                            <input
                                name="BuyerEmail"
                                type="email"
                                placeholder="email@example.com"
                                value={formData.BuyerEmail}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    fontSize: '16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    transition: 'all 0.2s',
                                    background: '#fff',
                                    WebkitAppearance: 'none'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#03c75a';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(3, 199, 90, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* 하단 푸터 */}
                {/* <footer style={{
                    background: '#f7f8fa',
                    borderTop: '1px solid #e8eaed',
                    padding: '32px 16px',
                    marginTop: '40px'
                }}>
                    <div style={{ width: '100%', margin: '0 auto' }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            fontSize: '12px',
                            color: '#8e8e93'
                        }}>
                            <div style={{
                                display: 'flex',
                                gap: '20px',
                                flexWrap: 'wrap',
                                alignItems: 'center'
                            }}>
                                <a href="#" style={{ 
                                    color: '#666', 
                                    textDecoration: 'none',
                                    fontSize: '12px',
                                    transition: 'color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.color = '#03c75a'}
                                onMouseOut={(e) => e.target.style.color = '#666'}
                                >이용약관</a>
                                <span style={{ color: '#d1d5db' }}>|</span>
                                <a href="#" style={{ 
                                    color: '#666', 
                                    textDecoration: 'none', 
                                    fontWeight: '500',
                                    fontSize: '12px',
                                    transition: 'color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.color = '#03c75a'}
                                onMouseOut={(e) => e.target.style.color = '#666'}
                                >개인정보처리방침</a>
                                <span style={{ color: '#d1d5db' }}>|</span>
                                <a href="#" style={{ 
                                    color: '#666', 
                                    textDecoration: 'none',
                                    fontSize: '12px',
                                    transition: 'color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.color = '#03c75a'}
                                onMouseOut={(e) => e.target.style.color = '#666'}
                                >고객센터</a>
                            </div>
                            <div style={{
                                paddingTop: '20px',
                                borderTop: '1px solid #e8eaed',
                                lineHeight: '1.8'
                            }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <strong style={{ color: '#333', fontSize: '13px', fontWeight: '600' }}>주식회사 빠른</strong>
                                </div>
                                <div style={{ color: '#8e8e93', fontSize: '12px' }}>
                                    대표이사: 홍길동 | 사업자등록번호: 123-45-67890<br/>
                                    주소: 서울특별시 강남구 테헤란로 123<br/>
                                    고객센터: 1588-0000 | 이메일: help@payblue.com
                                </div>
                                <div style={{ marginTop: '12px', color: '#8e8e93', fontSize: '11px' }}>
                                    Copyright © 주식회사 빠른. All rights reserved.
                                </div>
                            </div>
                        </div>
                    </div>
                </footer> */}

                {/* 하단 고정 버튼 */}
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: '#fff',
                    padding: '16px',
                    boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
                    zIndex: 1000,
                    borderTop: '1px solid #e8eaed',
                    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))'
                }}>
                    <button
                        onClick={onSubmitPayment}
                        disabled={isDisabled}
                        style={{
                            width: '100%',
                            padding: '18px',
                            fontSize: '17px',
                            fontWeight: '600',
                            color: '#fff',
                            background: isDisabled ? '#d1d5db' : 'linear-gradient(135deg, #03c75a 0%, #02b350 100%)',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: isDisabled ? 'none' : '0 4px 12px rgba(3, 199, 90, 0.3)',
                            letterSpacing: '-0.3px',
                            WebkitTapHighlightColor: 'transparent'
                        }}
                        onMouseOver={(e) => {
                            if (!isDisabled) {
                                e.target.style.background = 'linear-gradient(135deg, #02b350 0%, #019944 100%)';
                                e.target.style.boxShadow = '0 6px 16px rgba(3, 199, 90, 0.4)';
                                e.target.style.transform = 'translateY(-1px)';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isDisabled) {
                                e.target.style.background = 'linear-gradient(135deg, #03c75a 0%, #02b350 100%)';
                                e.target.style.boxShadow = '0 4px 12px rgba(3, 199, 90, 0.3)';
                                e.target.style.transform = 'translateY(0)';
                            }
                        }}
                    >
                        {isSubmitting ? "결제중..." : `${displayAmount || "0"}원 결제하기`}
                    </button>
                </div>
            </div>

        </>
    );
}

export default SmsPaymentPublic;

